import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { simulationSessionService } from "../services/simulationSessionService";
import { simulationSessionDisplayId } from "./SimulationSessionCommonStore";
import { SimulationSession } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class SimulationSessionViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = simulationSessionService;
  simulationSessionInstance: SimulationSession | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  statusValue: string = "";
  modeValue: string = "";
  labelValue: string = "";
  entryPipelineValue: string = "";
  scenarioValue: string = "{}";
  resolvedGatesValue: string = "{}";
  currentGateValue: string = "";
  correlationIdValue: string = "";
  relatedEntityTypeValue: string = "";
  relatedEntityIdValue: string = "";
  startedAtValue: Date | null = null;
  completedAtValue: Date | null = null;
  triggeredByService = userService;
  triggeredBySearchValue: string = "";
  triggeredByIdValue: string = "";
  triggeredBySelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadSimulationSessionById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.simulationSessionOne({ id });
    this.simulationSessionInstance = result.simulationSessionOne;
    if (this.simulationSessionInstance) {
      this.loadValues(this.simulationSessionInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.simulationSessionInstance) {
      return simulationSessionDisplayId(
        this.simulationSessionInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SimulationSessionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "triggeredBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.triggeredBySelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get triggeredByValue() {
    if (this.triggeredBySelectedValue) {
      return {
        id: this.triggeredBySelectedValue.id,
        displayId: userDisplayId(this.triggeredBySelectedValue, this.langStore),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.simulationSessionInstance = null;
    this.statusValue = "";
    this.modeValue = "";
    this.labelValue = "";
    this.entryPipelineValue = "";
    this.scenarioValue = "{}";
    this.resolvedGatesValue = "{}";
    this.currentGateValue = "";
    this.correlationIdValue = "";
    this.relatedEntityTypeValue = "";
    this.relatedEntityIdValue = "";
    this.startedAtValue = null;
    this.completedAtValue = null;
    this.triggeredBySearchValue = "";
    this.triggeredByIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelSimulationSession() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editSimulationSession() {
    const id = this.simulationSessionInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/simulationSession/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printSimulationSession(handlePrint: () => void): void {
    handlePrint();
  }

  *closeSimulationSession(): any {
    yield this.close();
  }

  loadValues(instance: SimulationSession) {
    this.idValue = instance.id;
    this.statusValue = instance.status;
    this.modeValue = instance.mode;
    this.labelValue = instance.label || "";
    this.entryPipelineValue = instance.entryPipeline;
    this.scenarioValue = instance.scenario || "";
    this.resolvedGatesValue = instance.resolvedGates || "";
    this.currentGateValue = instance.currentGate || "";
    this.correlationIdValue = instance.correlationId;
    this.relatedEntityTypeValue = instance.relatedEntityType || "";
    this.relatedEntityIdValue = instance.relatedEntityId || "";
    this.startedAtValue = new Date(instance.startedAt);
    this.completedAtValue = instance.completedAt
      ? new Date(instance.completedAt)
      : null;
    this.triggeredBySelectedValue = instance.triggeredBy;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadSimulationSessionById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
